import { IconType } from 'react-icons'
import {
    FiCalendar,
    FiBell,
    FiMessageCircle,
    FiBookmark,
    FiVideo,
    FiMusic,
    FiFolder,
    FiGrid,
    FiLayers,
    FiHelpCircle
} from 'react-icons/fi'
import { AdminResource } from '@/shared/api/rest/config'

/** Canonical age groups (mirrors heritage-app-backend AgeGroupEnum). */
export const AGE_GROUPS = [
    'Toddlers',
    'PreSchool',
    'SchoolAge',
    'Samuels',
    'Teenagers'
] as const

export type AdminFieldType =
    | 'text'
    | 'textarea'
    | 'number'
    | 'url'
    | 'image'
    | 'file'
    | 'color'
    | 'youtube'
    | 'tags'
    | 'ageGroups'
    | 'enum'
    | 'reference'
    | 'verse'
    | 'songs'

export interface AdminField {
    name: string
    label: string
    type: AdminFieldType
    required?: boolean
    placeholder?: string
    helper?: string
    /** enum options */
    options?: readonly string[]
    /** reference: the resource to load options from */
    refResource?: AdminResource
    /** reference: build the option label from a record */
    refLabel?: (item: Record<string, unknown>) => string
    /** file/image: accepted file kind for the dropzone */
    accept?: 'image' | 'audio' | 'any'
}

export interface AdminColumn {
    key: string
    label: string
    render?: (item: Record<string, unknown>) => string
}

export interface ResourceConfig {
    resource: AdminResource
    label: string
    singular: string
    icon: IconType
    /** Short tagline shown under the page title. */
    description: string
    /** A sentence or two explaining where this appears in the app, for context. */
    appContext: string
    columns: AdminColumn[]
    fields: AdminField[]
    /** Optional age-group list filter (weekly-bundles, anthems). */
    ageGroupFilter?: boolean
    /** Nested resources filter the list by a parent id. */
    parentFilter?: {
        param: 'categoryId' | 'levelId'
        refResource: AdminResource
        label: string
        refLabel: (item: Record<string, unknown>) => string
    }
}

const truncate = (v: unknown, n = 60) => {
    const s = v == null ? '' : String(v)
    return s.length > n ? `${s.slice(0, n)}…` : s
}
const arr = (v: unknown) => (Array.isArray(v) ? v.join(', ') : '')

export const RESOURCES: ResourceConfig[] = [
    {
        resource: 'weekly-bundles',
        icon: FiCalendar,
        label: 'Weekly Bundles',
        singular: 'Weekly Bundle',
        description: 'The weekly lesson pack for the Kids Zone home screen.',
        appContext:
            'Each bundle powers a week on the Anointed Kids Zone home: the lesson topic, scripture, memory verse and the “Mama’s Song” hero card. Children see the bundle that matches their age group.',
        ageGroupFilter: true,
        columns: [
            { key: 'title', label: 'Title' },
            { key: 'weekNumber', label: 'Week' },
            { key: 'topic', label: 'Topic' },
            { key: 'ageGroups', label: 'Age Groups', render: (i) => arr(i.ageGroups) }
        ],
        fields: [
            {
                name: 'title',
                label: 'Title',
                type: 'text',
                required: true,
                placeholder: 'e.g. Week 25: Obedience',
                helper: 'A short name for this week’s lesson.'
            },
            {
                name: 'weekNumber',
                label: 'Week Number',
                type: 'number',
                placeholder: '25'
            },
            {
                name: 'topic',
                label: 'Topic',
                type: 'text',
                placeholder: 'e.g. Obedience'
            },
            {
                name: 'description',
                label: 'Description',
                type: 'textarea',
                helper: 'A short summary of the lesson.'
            },
            {
                name: 'scriptureReference',
                label: 'Scripture Reference',
                type: 'text',
                placeholder: 'e.g. 1 Samuel 15:22'
            },
            {
                name: 'memoryVerse',
                label: 'Memory Verse',
                type: 'verse',
                helper: 'The verse children will memorise this week.'
            },
            {
                name: 'thumbnailUrl',
                label: 'Hero Card Image',
                type: 'image',
                helper: 'The image shown on the week’s hero card.'
            },
            {
                name: 'mamasSongVideoId',
                label: "Mama's Song (YouTube)",
                type: 'youtube',
                helper: 'Paste the YouTube link — we’ll pick out the video for you.'
            },
            {
                name: 'ageGroups',
                label: 'Age Groups',
                type: 'ageGroups',
                helper: 'Which children see this bundle. Leave empty to show to all.'
            }
        ]
    },
    {
        resource: 'announcements',
        icon: FiBell,
        label: 'Announcements',
        singular: 'Announcement',
        description: 'Short notices that scroll across the Kids Zone.',
        appContext:
            'Announcements appear in the moving ticker on the Anointed Kids Zone home — great for reminders and quick news.',
        columns: [
            { key: 'text', label: 'Text', render: (i) => truncate(i.text) },
            { key: 'color', label: 'Color' }
        ],
        fields: [
            {
                name: 'text',
                label: 'Announcement',
                type: 'textarea',
                required: true,
                helper: 'Keep it short — it scrolls past quickly.'
            },
            {
                name: 'color',
                label: 'Highlight Colour',
                type: 'color',
                placeholder: '#FF00FF',
                helper: 'Optional. Pick the ticker highlight colour.'
            }
        ]
    },
    {
        resource: 'quotes',
        icon: FiMessageCircle,
        label: 'Quotes (Papa Says)',
        singular: 'Quote',
        description: 'Inspirational quotes for the “Papa Says” card.',
        appContext:
            'A random quote is shown in the “Papa Says” card on the Anointed Kids Zone home each time a child opens it.',
        columns: [
            { key: 'text', label: 'Quote', render: (i) => truncate(i.text) },
            { key: 'author', label: 'Author' }
        ],
        fields: [
            { name: 'text', label: 'Quote', type: 'textarea', required: true },
            {
                name: 'author',
                label: 'Author',
                type: 'text',
                placeholder: 'e.g. Papa'
            }
        ]
    },
    {
        resource: 'covenants',
        icon: FiBookmark,
        label: 'Covenants',
        singular: 'Covenant',
        description: 'Declarations children read and confess.',
        appContext:
            'Covenants are the declarations shown in the Missions / Covenant area of the Kids Zone. Tags help group related declarations.',
        columns: [
            { key: 'text', label: 'Text', render: (i) => truncate(i.text) },
            { key: 'tags', label: 'Tags', render: (i) => arr(i.tags) }
        ],
        fields: [
            { name: 'text', label: 'Covenant', type: 'textarea', required: true },
            {
                name: 'tags',
                label: 'Tags',
                type: 'tags',
                helper: 'Type a tag and press Enter. Used to group covenants.'
            }
        ]
    },
    {
        resource: 'videos',
        icon: FiVideo,
        label: 'Videos',
        singular: 'Video',
        description: 'Videos children can watch in the Kids Zone.',
        appContext:
            'Videos appear in the Kids Zone video sections. Use the Category to control where a video shows up.',
        columns: [
            { key: 'title', label: 'Title' },
            { key: 'category', label: 'Category' },
            { key: 'videoId', label: 'Video ID' }
        ],
        fields: [
            { name: 'title', label: 'Title', type: 'text', required: true },
            {
                name: 'videoId',
                label: 'YouTube Video',
                type: 'youtube',
                helper: 'Paste the YouTube link — we’ll pick out the video for you.'
            },
            {
                name: 'url',
                label: 'Other Video URL',
                type: 'url',
                helper: 'Only needed if the video is not on YouTube.'
            },
            {
                name: 'thumbnail',
                label: 'Thumbnail',
                type: 'image',
                helper: 'Automatically uses the YouTube thumbnail. Upload only if you want to override it.'
            },
            {
                name: 'category',
                label: 'Category',
                type: 'text',
                helper: 'Groups the video in the app (e.g. “songs”, “bible”).'
            }
        ]
    },
    {
        resource: 'anthems',
        icon: FiMusic,
        label: 'Anthems',
        singular: 'Anthem',
        description: 'Songs with audio and lyrics.',
        appContext:
            'Anthems appear in the Kids Zone songs/anthems area with their cover art, audio and lyrics. They are targeted by age group.',
        ageGroupFilter: true,
        columns: [
            { key: 'title', label: 'Title' },
            { key: 'artist', label: 'Artist' },
            { key: 'ageGroups', label: 'Age Groups', render: (i) => arr(i.ageGroups) }
        ],
        fields: [
            { name: 'title', label: 'Title', type: 'text', required: true },
            { name: 'artist', label: 'Artist', type: 'text' },
            {
                name: 'audioUrl',
                label: 'Audio File',
                type: 'file',
                accept: 'audio',
                helper: 'Upload the anthem audio file.'
            },
            { name: 'coverImage', label: 'Cover Art', type: 'image' },
            {
                name: 'duration',
                label: 'Duration (seconds)',
                type: 'number',
                placeholder: '180'
            },
            { name: 'lyrics', label: 'Lyrics', type: 'textarea' },
            {
                name: 'ageGroups',
                label: 'Age Groups',
                type: 'ageGroups',
                helper: 'Which children see this anthem. Leave empty to show to all.'
            }
        ]
    },
    {
        resource: 'media-items',
        icon: FiFolder,
        label: 'Media Library',
        singular: 'Media Item',
        description: 'Worship videos, playlists, books and Bible stories.',
        appContext:
            'Media items power the Worship, Songs, Books and Bible sections of the Kids Zone. The Type decides which section it appears in.',
        columns: [
            { key: 'title', label: 'Title' },
            { key: 'type', label: 'Type' }
        ],
        fields: [
            { name: 'title', label: 'Title', type: 'text', required: true },
            {
                name: 'type',
                label: 'Type',
                type: 'enum',
                required: true,
                options: ['VIDEO', 'PLAYLIST', 'DOCUMENT', 'STORY'],
                helper: 'Worship video, a song playlist, a book (document) or an animated Bible story.'
            },
            {
                name: 'url',
                label: 'Link',
                type: 'url',
                helper: 'The video, document or story link.'
            },
            {
                name: 'thumbnail',
                label: 'Thumbnail',
                type: 'image',
                helper: 'Upload the image shown for this media item.'
            },
            {
                name: 'songs',
                label: 'Songs',
                type: 'songs',
                helper: 'Only for playlists — add each song with its title and audio link.'
            }
        ]
    },
    {
        resource: 'game-categories',
        icon: FiGrid,
        label: 'Game Categories',
        singular: 'Game Category',
        description: 'The top-level groups for the quiz games.',
        appContext:
            'Categories are the cards children pick from on the Games screen (e.g. “Bible Heroes”). Each category holds Levels, and each level holds Questions.',
        columns: [
            { key: 'title', label: 'Title' },
            {
                key: 'description',
                label: 'Description',
                render: (i) => truncate(i.description)
            },
            { key: 'color', label: 'Color' }
        ],
        fields: [
            {
                name: 'title',
                label: 'Title',
                type: 'text',
                required: true,
                placeholder: 'e.g. Bible Heroes'
            },
            { name: 'description', label: 'Description', type: 'textarea' },
            {
                name: 'color',
                label: 'Card Colour',
                type: 'color',
                placeholder: '#FFD700',
                helper: 'Optional. Pick the game category card colour.'
            }
        ]
    },
    {
        resource: 'game-levels',
        icon: FiLayers,
        label: 'Game Levels',
        singular: 'Game Level',
        description: 'The levels inside each game category.',
        appContext:
            'Levels are the stages within a game category. Children unlock them in order. Pick which category a level belongs to.',
        parentFilter: {
            param: 'categoryId',
            refResource: 'game-categories',
            label: 'Category',
            refLabel: (i) => String(i.title ?? i._id)
        },
        columns: [
            { key: 'title', label: 'Title' },
            { key: 'position', label: 'Order' },
            { key: 'rewardPoints', label: 'Reward Points' }
        ],
        fields: [
            {
                name: 'categoryId',
                label: 'Category',
                type: 'reference',
                required: true,
                refResource: 'game-categories',
                refLabel: (i) => String(i.title ?? i._id),
                helper: 'Which game category this level belongs to.'
            },
            {
                name: 'title',
                label: 'Title',
                type: 'text',
                required: true,
                placeholder: 'e.g. Level 1'
            },
            {
                name: 'position',
                label: 'Order',
                type: 'number',
                helper: 'Lower numbers appear first.'
            },
            {
                name: 'rewardPoints',
                label: 'Reward Points',
                type: 'number',
                helper: 'Points a child earns for completing this level.'
            }
        ]
    },
    {
        resource: 'game-questions',
        icon: FiHelpCircle,
        label: 'Game Questions',
        singular: 'Game Question',
        description: 'The questions inside each game level.',
        appContext:
            'Questions are what children answer to complete a level. Pick the level, write the question, set the type and the correct answer.',
        parentFilter: {
            param: 'levelId',
            refResource: 'game-levels',
            label: 'Level',
            refLabel: (i) => String(i.title ?? i._id)
        },
        columns: [
            { key: 'text', label: 'Question', render: (i) => truncate(i.text) },
            { key: 'type', label: 'Type' },
            { key: 'correctAnswer', label: 'Answer' }
        ],
        fields: [
            {
                name: 'levelId',
                label: 'Level',
                type: 'reference',
                required: true,
                refResource: 'game-levels',
                refLabel: (i) => String(i.title ?? i._id),
                helper: 'Which level this question belongs to.'
            },
            { name: 'text', label: 'Question', type: 'textarea', required: true },
            {
                name: 'type',
                label: 'Question Type',
                type: 'enum',
                required: true,
                options: [
                    'MULTIPLE_CHOICE',
                    'TRUE_FALSE',
                    'IMAGE_GUESS',
                    'FILL_IN_BLANK',
                    'AUDIO_GUESS'
                ],
                helper: 'How the child answers.'
            },
            {
                name: 'options',
                label: 'Answer Options',
                type: 'tags',
                helper: 'Type each answer choice and press Enter (for multiple-choice).'
            },
            {
                name: 'correctAnswer',
                label: 'Correct Answer',
                type: 'text',
                helper: 'Must exactly match one of the options above.'
            },
            {
                name: 'mediaUrl',
                label: 'Question Image / Audio',
                type: 'file',
                accept: 'any',
                helper: 'For picture or audio questions.'
            },
            { name: 'points', label: 'Points', type: 'number' },
            {
                name: 'position',
                label: 'Order',
                type: 'number',
                helper: 'Lower numbers appear first.'
            }
        ]
    }
]

export const getResourceConfig = (resource: string): ResourceConfig | undefined =>
    RESOURCES.find((r) => r.resource === resource)
